import type { TemplateStatus } from '@cognite/apm-client';
import { makeToast } from '@cognite/cogs-lab';
import { TEMPLATE_STATUS } from '@infield/constants/common';
import { FETCH_MODE } from '@infield/constants/common';
import { LOCIZE_NAMESPACES, useTranslation } from '@infield/features/i18n';
import { TemplateListMobile } from '@infield/features/template';
import { ComponentErrorBoundary } from '@infield/features/ui/component-error-boundary';
import type { FC } from 'react';
import { useContext, useEffect, useMemo } from 'react';

import * as S from './elements';
import { TemplatesMobileContext } from './templates-mobile.context';

export const TemplatesMobile: FC = () => {
  const templateStatus = TEMPLATE_STATUS.READY as TemplateStatus;
  const { t } = useTranslation(LOCIZE_NAMESPACES.template);

  const {
    useTemplateListQuery,
    useTemplatesSearch,
    useTemplateSelection,
    useSearchQuery,
  } = useContext(TemplatesMobileContext);

  const { selectedTemplate, setSelectedTemplate } = useTemplateSelection();
  const { debouncedQuery, fetchMode } = useSearchQuery();
  const {
    data: templatesList = [],
    isInitialLoading: isListLoading,
    isFetching: isListFetching,
    error: listError,
  } = useTemplateListQuery(templateStatus);

  const {
    data: templatesSearchList = [],
    isInitialLoading: isSearchLoading,
    isFetching: isSearchFetching,
    error: searchError,
  } = useTemplatesSearch({
    query: debouncedQuery,
    properties: ['title'],
    enabled: fetchMode === FETCH_MODE.SEARCH,
    limit: 100,
    templateStatus,
  });

  const isInitialLoading =
    fetchMode === FETCH_MODE.SEARCH ? isSearchLoading : isListLoading;
  const isFetching =
    fetchMode === FETCH_MODE.SEARCH ? isSearchFetching : isListFetching;
  const error = fetchMode === FETCH_MODE.SEARCH ? searchError : listError;

  const templates = useMemo(() => {
    if (fetchMode === FETCH_MODE.SEARCH) {
      return templatesSearchList;
    }
    return templatesList;
  }, [fetchMode, templatesSearchList, templatesList]);

  useEffect(() => {
    // Re-select the matching template from the updated list,
    // if one was previously selected and data has finished loading.
    if (
      templates.length > 0 &&
      !isFetching &&
      !isInitialLoading &&
      fetchMode === FETCH_MODE.LIST &&
      selectedTemplate
    ) {
      setSelectedTemplate(
        templates.find((t) => t.externalId === selectedTemplate.externalId)
      );
    }
  }, [
    templates,
    isInitialLoading,
    fetchMode,
    isFetching,
    setSelectedTemplate,
    selectedTemplate,
  ]);

  useEffect(() => {
    if (error) {
      makeToast({
        type: 'danger',
        body: t(
          'TEMPLATE_LIST_QUERY_ERROR',
          'Could not load any templates. Try again later.'
        ),
      });
    }
  }, [error, t]);

  return (
    <ComponentErrorBoundary sentryTag="TemplatesMobile">
      <S.Container>
        <TemplateListMobile
          templates={templates}
          isLoading={isInitialLoading}
        />
      </S.Container>
    </ComponentErrorBoundary>
  );
};
